import {SerializedStyles} from '@emotion/react';
import {css, useTheme, IconButton, IconButtonProps, PopperProps, Typography} from '@mui/material';
import SvgIcon, {SvgIconProps} from '@mui/material/SvgIcon';
import React, {CSSProperties} from 'react';
import {useIntl} from 'react-intl';
import {getIconComponentByName} from 'src/js/common/icons/icon_helpers';
import {GetStylesFunction} from 'src/js/common/types';
import {TranslateUtils} from 'src/js/common/utils';
import {useMuiAnchor} from 'src/js/hooks';

import {DlMuiPopper} from './DlMuiPopper';

interface OwnProps extends IconButtonProps {
  iconName: string;
  iconTitle?: string;
  inputCss?: SerializedStyles;
  svgIconInputCss?: SerializedStyles;
  svgIconStyle?: CSSProperties;
  svgIconProps?: SvgIconProps;
  useFixedViewBox?: boolean | number | string;
  noButtonStyles?: boolean;
  preserveAspectRatio?: string;
  isNavigationOpen?: boolean;
  isTooltipInNavigationMenu?: boolean;
  /** Popper placement of the iconTitle tooltip; default 'bottom'. Use e.g. 'top' when the
   * default position would cover content below the icon (dropdown options, ...). */
  tooltipPlacement?: PopperProps['placement'];
  /** Extra Popper props for the iconTitle tooltip (e.g. an offset modifier when the tooltip
   * anchored to a small in-field icon would cover the field's floating label). */
  tooltipPopperProps?: Partial<PopperProps>;
}

type Props = OwnProps;

export const DlMuiDynamicIcon: React.FC<Props> = (props) => {
  const {
    iconName,
    inputCss,
    iconTitle,
    svgIconInputCss,
    svgIconStyle,
    svgIconProps,
    useFixedViewBox,
    onClick,
    disabled,
    noButtonStyles,
    preserveAspectRatio,
    isNavigationOpen,
    isTooltipInNavigationMenu,
    tooltipPlacement,
    tooltipPopperProps,
    ...restProps
  } = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({
    theme,
    params: {inputCss, svgIconInputCss, onClick, disabled, noButtonStyles},
  });

  const translatedIconTitle = intl && iconTitle && TranslateUtils.translate(iconTitle, intl);

  const hasTooltip = !!translatedIconTitle;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl, forceCloseTooltip} = useMuiAnchor(hasTooltip);

  let RenderedIconComponent;

  if (iconName) {
    RenderedIconComponent = getIconComponentByName(iconName);
  }

  const useFixedViewBoxNumber = useFixedViewBox === true ? 24 : useFixedViewBox || 24;
  let fixedViewBox: string | undefined;
  switch (useFixedViewBox?.constructor?.name) {
    case 'Number':
      fixedViewBox = `0 0 ${useFixedViewBoxNumber} ${useFixedViewBoxNumber}`;
      break;
    case 'String':
      fixedViewBox = useFixedViewBox as string;
      break;
    default:
      fixedViewBox = '0 0 24 24';
      break;
  }

  return (
    <>
      <IconButton
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        css={styles.iconButton}
        onClick={
          disabled
            ? undefined
            : (el) => {
                forceCloseTooltip();
                if (onClick) {
                  onClick(el);
                }
              }
        }
        data-icon-name={iconName}
        {...restProps}
      >
        <SvgIcon
          css={styles.svgIcon}
          component={RenderedIconComponent}
          inheritViewBox={!useFixedViewBox}
          viewBox={useFixedViewBox ? fixedViewBox : undefined}
          style={svgIconStyle}
          preserveAspectRatio={preserveAspectRatio}
          {...svgIconProps}
        />
      </IconButton>
      <DlMuiPopper
        hasTooltip={hasTooltip}
        anchorEl={anchorEl}
        open={open}
        placement={tooltipPlacement}
        isTooltipInNavigationMenu={isTooltipInNavigationMenu}
        isNavigationOpen={isNavigationOpen}
        {...tooltipPopperProps}
      >
        <Typography variant={'popper'}>{translatedIconTitle}</Typography>
      </DlMuiPopper>
    </>
  );
};

DlMuiDynamicIcon.displayName = 'DlMuiDynamicIcon';

const getStyles: GetStylesFunction = (options) => {
  const onClick = options?.params?.onClick;
  const disabled = options?.params?.disabled;
  const noButtonStyles = options?.params?.noButtonStyles;
  return {
    iconButton: css(
      {
        'cursor': onClick && !disabled ? 'pointer' : 'default !important',
        ...(onClick && !noButtonStyles
          ? {
              'width': 24,
              'height': 24,
              'border': '1px solid var(--dlThemeGray-300)',
              'borderRadius': 8,
              'background': `${disabled ? 'var(--dlThemeGray-100)' : '#fff'}`,
              'color': `${disabled ? 'var(--dlThemeGray-500)' : 'var(--dlTheme-700)'}`,
              '&:hover': {
                background: 'var(--dlThemeGray-100)',
              },
              '&:active': {
                background: disabled ? 'var(--dlThemeGray-100)' : 'var(--dlThemeGray-300)',
              },
            }
          : {}),
        '&.Mui-disabled': {
          backgroundColor: 'unset',
        },
      },
      options?.params?.inputCss
    ),
    svgIcon: css(
      {
        cursor: onClick && !disabled ? 'pointer' : 'default',
      },
      options?.params?.svgIconInputCss
    ),
  };
};
